import { useState, useEffect } from "react";
import {
  Activity, RefreshCw, CheckCircle, XCircle, Loader2,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HEALTH_COLORS = {
  green:  { bg: "bg-emerald-500/20", text: "text-emerald-300", label: "Excellent" },
  yellow: { bg: "bg-amber-500/20",   text: "text-amber-300",   label: "Acceptable" },
  orange: { bg: "bg-orange-500/20",  text: "text-orange-300",  label: "Dégradé"    },
  red:    { bg: "bg-red-500/20",     text: "text-red-300",     label: "Critique"   },
};


export default function GouvNebula() {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [latestRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/nebula/latest`),
        fetch(`${API_URL}/api/nebula/history?limit=10`),
      ]);
      if (latestRes.ok) {
        const d = await latestRes.json();
        setReport(d.report);
      }
      if (historyRes.ok) {
        const d = await historyRes.json();
        setHistory(d.items || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await fetch(`${API_URL}/api/nebula/run?trigger=manual`, { method: "POST" });
      setTimeout(() => { loadData(); setRunning(false); }, 4000);
    } catch (e) { console.error(e); setRunning(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const colorCfg = HEALTH_COLORS[report?.health_color || "red"];
  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="space-y-6" data-testid="nebula-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">
            🛰 Nebula — Gardien Qualité OmniHub
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            {report
              ? `Dernier run : ${fmtDate(report.completed_at)}`
              : "Aucun run encore"}
          </p>
        </div>
        <Button
          onClick={runNow}
          disabled={running}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="nebula-run-now"
        >
          {running
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <RefreshCw className="w-4 h-4 mr-2" />}
          Lancer un run
        </Button>
      </div>

      {!report ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
          <Activity className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">
            Aucun rapport Nebula. Cliquer « Lancer un run » pour démarrer.
          </p>
        </div>
      ) : (
        <>
          {/* Score global */}
          <div className={`${colorCfg.bg} border border-zinc-700 rounded-xl p-6`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-zinc-400 text-sm mb-1">Score de santé OH</p>
                <p className={`text-5xl font-bold ${colorCfg.text}`}>
                  {report.score_global}%
                </p>
                <p className={`text-sm mt-1 ${colorCfg.text}`}>{colorCfg.label}</p>
              </div>
              <div className="text-sm text-zinc-300 space-y-1">
                <p>✅ {report.summary?.passed || 0} checks OK</p>
                <p>❌ {report.summary?.failed || 0} échecs</p>
                <p>⏱ {report.duration_ms}ms</p>
                {report.commit_sha && (
                  <p className="text-xs text-zinc-500">
                    commit {report.commit_sha.slice(0, 8)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Checks détaillés */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <h2 className="px-4 py-3 border-b border-zinc-800 text-white font-semibold">
              Smoke endpoints ({(report.checks || []).length})
            </h2>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left px-4 py-3 text-sm text-zinc-400">Endpoint</th>
                    <th className="text-left px-4 py-3 text-sm text-zinc-400">Statut</th>
                    <th className="text-left px-4 py-3 text-sm text-zinc-400">Latence</th>
                    <th className="text-left px-4 py-3 text-sm text-zinc-400">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.checks || []).map((c, i) => (
                    <tr key={i} className="border-b border-zinc-800/50">
                      <td className="px-4 py-2 text-sm text-white">{c.name}</td>
                      <td className="px-4 py-2">
                        {c.passed ? (
                          <Badge className="bg-emerald-500/20 text-emerald-300">
                            <CheckCircle className="w-3 h-3 mr-1" />OK
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-300">
                            <XCircle className="w-3 h-3 mr-1" />KO
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-zinc-400">{c.latency_ms}ms</td>
                      <td className="px-4 py-2 text-xs text-zinc-500 max-w-md truncate" title={c.error || ""}>
                        {c.error || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cartes */}
            <div className="md:hidden p-3 space-y-2">
              {(report.checks || []).map((c, i) => (
                <div key={i} className="bg-zinc-800/30 border border-zinc-800 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm text-white truncate flex-1">{c.name}</p>
                    {c.passed
                      ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span>{c.latency_ms}ms</span>
                    {c.error && (
                      <span className="text-red-400 truncate flex-1">• {c.error}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Historique récent */}
          {history.length > 1 && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-white font-semibold mb-3">Historique récent</h2>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {history.slice(0, 10).map((h) => {
                  const cfg = HEALTH_COLORS[h.health_color] || HEALTH_COLORS.red;
                  return (
                    <div key={h.id} className="shrink-0 bg-zinc-800/30 rounded p-2 min-w-[110px]">
                      <p className={`text-2xl font-bold ${cfg.text}`}>
                        {h.score_global}%
                      </p>
                      <p className="text-xs text-zinc-500">{fmtDate(h.completed_at)}</p>
                      <p className="text-xs text-zinc-600 capitalize">{h.trigger}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
